import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  type ActionHandler,
  Create,
  EmptyStation,
  MessageBar,
  type Nullable,
  SingleLineTextField,
} from '@axinom/mosaic-ui';
import clsx from 'clsx';
import { config } from 'config';
import { Field } from 'formik';
import type { BreadcrumbResolver } from 'models/breadcrumb';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  CreateAzureStorageContainerDocument,
  type CreateAzureStorageContainerMutation,
  type CreateAzureStorageContainerMutationVariables,
} from '../../../../generated/hosting-service';
import { getManagedServiceAvailabilityMessage } from '../../../../util/managedServiceUtil';
import { environmentApolloClient } from '../../../environmentApolloClient';
import classes from './ContainerCreate.module.scss';
import { containerCreateSchema } from './container-create-validation-schema';

type FormData = CreateAzureStorageContainerMutationVariables['input'];

type SubmitResponse = Nullable<
  CreateAzureStorageContainerMutation['createAzureStorageContainer']
>;

export const StorageAccountContainerCreate: React.FC = () => {
  const { envId, storageAccountName, storageType } = useParams<{
    envId: string;
    storageAccountName: string;
    storageType: string;
    action: string;
  }>();

  const [loading, setLoading] = useState<boolean>(false);
  const [serviceStatusMessage, setServiceStatusMessage] = useState<
    string | null
  >(null);

  // Check the status of hosting-service
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        const statusMessage = await getManagedServiceAvailabilityMessage(
          ManagedServiceId.HOSTING_SERVICE,
          envId,
        );
        setServiceStatusMessage(statusMessage);
      } finally {
        setLoading(false);
      }
    })();
  }, [envId]);

  const saveData = useCallback(
    async (formData: FormData): Promise<SubmitResponse> => {
      const envApolloClient = environmentApolloClient(
        envId,
        new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
      );

      const response = await envApolloClient.mutate<
        CreateAzureStorageContainerMutation,
        CreateAzureStorageContainerMutationVariables
      >({
        mutation: CreateAzureStorageContainerDocument,
        variables: {
          input: {
            name: formData.name,
            provider: storageType,
            storageAccountName: storageAccountName,
          },
        },
        fetchPolicy: 'no-cache',
      });
      return response.data?.createAzureStorageContainer ?? null;
    },
    [envId, storageAccountName, storageType],
  );

  const history = useHistory();

  const onProceed = useCallback<ActionHandler<FormData, SubmitResponse>>(
    ({ submitResponse }) => {
      const container = submitResponse?.container;
      if (container) {
        history.push(
          `/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config/storage/${storageType}/${storageAccountName}/containers/${container?.name}`,
        );
      }
    },
    [envId, history, storageAccountName, storageType],
  );

  const Form: React.FC = () => {
    return (
      <Field
        name="name"
        label="Name"
        as={SingleLineTextField}
        className={clsx(classes.validation_wrap)}
      />
    );
  };

  return (
    <>
      {serviceStatusMessage ? (
        <EmptyStation title="New Container">
          <MessageBar type="warning" title={serviceStatusMessage}></MessageBar>
        </EmptyStation>
      ) : (
        <Create<FormData, SubmitResponse>
          title="New Container"
          validationSchema={containerCreateSchema}
          saveData={saveData}
          onProceed={onProceed}
          cancelNavigationUrl={`/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config/storage/${storageType}/${storageAccountName}/containers`}
          initialData={{
            loading,
          }}
        >
          <Form />
        </Create>
      )}
    </>
  );
};

export const StorageAccountContainerCreateCrumb: BreadcrumbResolver = () =>
  'New Container';
